import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import { Login } from './pages/Login';
import { StudentDashboard } from './pages/StudentDashboard';
import { SubjectView } from './pages/SubjectView';
import { ChapterView } from './pages/ChapterView';
import { Viewer3D } from './pages/Viewer3D';
import { StudentProgress } from './pages/StudentProgress';
import { CameraScanner } from './pages/CameraScanner';
import { SuperAdminDashboard } from './pages/SuperAdminDashboard';
import { TenantAdminDashboard } from './pages/TenantAdminDashboard';
import { ContentAdminDashboard } from './pages/ContentAdminDashboard';
import { InstitutionAdminDashboard } from './pages/InstitutionAdminDashboard';
import { TenantSetupWizard } from './pages/TenantSetupWizard';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/login" />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Navigate to="/login" />} />
      
      <Route
        path="/student"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/scan"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <CameraScanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/subject/:subjectId"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <SubjectView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/chapter/:chapterId"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <ChapterView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/progress"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentProgress />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/viewer/:markerId"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <Viewer3D />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/super"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/super/tenant-setup"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <TenantSetupWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenant"
        element={
          <ProtectedRoute allowedRoles={['tenant_admin']}>
            <TenantAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/content"
        element={
          <ProtectedRoute allowedRoles={['content_admin']}>
            <ContentAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/institution"
        element={
          <ProtectedRoute allowedRoles={['institution_admin']}>
            <InstitutionAdminDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;